import React, { useMemo, useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { FieldLabel, inputClass, selectClass } from '../../create/shared/FieldLabel';
import { PickedUser, UserPicker } from '../../create/shared/UserPicker';
import { FoundUser } from '../../create/shared/useUserSearch';
import { useReferralCodeMutations, useReferralCodes } from '../api/useReferralCodes';
import { useCampaignOptions } from '../api/useReferralCampaigns';
import {
  CODE_PATTERN,
  CreatableCodeKind,
  CreateCodeBody,
  MAX_PAGE_SIZE,
  PAYOUT_RATE_MAX_CENTS,
  ReferralCode,
  UpdateCodeBody,
  CodeStatus,
} from '../api/types';
import { centsToDollars, dollarsToCents, formatCents } from '../format';
import { CodeKindBadge } from './controls';

const KIND_HELP: Record<CreatableCodeKind, string> = {
  promoter:
    'Pays a bound app account: the promoter earns signup coins plus the $ payout basis below. Requires a promoter.',
  campaign:
    'Analytics only: the referrer is credited nothing and no account is bound. Tag ownership with an ambassador ref instead.',
};

const AMBASSADOR_MAX = 120;

/**
 * Create / edit a referral code.
 *
 * The kind rules are the backend's (post-admin-referral-code) mirrored here so a
 * doomed request is caught before it is sent: `promoter` must bind an account
 * because the consumer pays that account; `campaign` must not, because a
 * campaign code credits nobody and an owner on one would read as "this person
 * gets paid" while being inert.
 *
 * On edit, `code` and `kind` are immutable (attributions and grants reference the
 * code; the kind selects the reward semantics), and a `personal` code allows only
 * status / ambassador edits — its owner, rate and deal are consumer-owned.
 *
 * The PUT is a diff: only fields the admin actually changed are sent, so an edit
 * to one field cannot silently rewrite another.
 */
export function CodeForm({
  env,
  existing,
  onCancel,
  onSaved,
}: {
  env: Env;
  /** Absent when minting a new code. */
  existing?: ReferralCode;
  onCancel: () => void;
  onSaved: (code: ReferralCode, message: string) => void;
}) {
  const isEdit = !!existing;
  const kind = existing?.kind ?? 'promoter';
  const isPersonal = kind === 'personal';
  const locked = isPersonal; // personal codes: status + ambassador only

  const [createKind, setCreateKind] = useState<CreatableCodeKind>('promoter');
  const effectiveKind: CreatableCodeKind | 'personal' = isEdit ? kind : createKind;

  const [code, setCode] = useState('');
  const [owner, setOwner] = useState<PickedUser | null>(
    existing?.owner
      ? {
          user_id: existing.owner.user_id,
          display_name: existing.owner.display_name,
          email: existing.owner.email,
          source: 'directory',
        }
      : null,
  );
  const [rate, setRate] = useState(centsToDollars(existing?.payout_rate_cents));
  const [campaignId, setCampaignId] = useState(existing?.deal_campaign?.id ?? '');
  const [ambassador, setAmbassador] = useState(existing?.ambassador_ref ?? '');
  const [status, setStatus] = useState<CodeStatus>(existing?.status ?? 'active');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const campaigns = useCampaignOptions(env);
  const { create, update, saving, error } = useReferralCodeMutations(env);

  // Promoters already bound to a code are the only place display names and
  // emails are available today, so they double as the user picker's name-search
  // set. See useUserSearch for why the picker needs one.
  const promoterCodes = useReferralCodes(env, { kind: 'promoter', limit: MAX_PAGE_SIZE });
  const directory: FoundUser[] = useMemo(() => {
    const seen = new Map<string, FoundUser>();
    for (const c of promoterCodes.data?.codes ?? []) {
      if (c.owner && !seen.has(c.owner.user_id)) {
        seen.set(c.owner.user_id, {
          user_id: c.owner.user_id,
          display_name: c.owner.display_name,
          email: c.owner.email,
          source: 'directory',
        });
      }
    }
    return [...seen.values()];
  }, [promoterCodes.data]);

  const parsedRate = dollarsToCents(rate);
  const rateInvalid =
    parsedRate !== null &&
    (Number.isNaN(parsedRate) || parsedRate < 0 || parsedRate > PAYOUT_RATE_MAX_CENTS);

  const validate = (): string | null => {
    if (!isEdit && code.trim() && !CODE_PATTERN.test(code.trim().toUpperCase())) {
      return "Code must be 3–32 characters of A–Z, 0–9, '-' or '_', starting with a letter or digit.";
    }
    if (rateInvalid) {
      return `Payout rate must be between $0 and ${formatCents(PAYOUT_RATE_MAX_CENTS)}.`;
    }
    if (effectiveKind === 'promoter' && !owner) {
      return 'Promoter codes must be bound to an app account — the consumer pays that account.';
    }
    if (ambassador.trim().length > AMBASSADOR_MAX) {
      return `Ambassador ref must be at most ${AMBASSADOR_MAX} characters.`;
    }
    return null;
  };

  const ownerFields = (): Pick<CreateCodeBody, 'owner_user_id' | 'owner_email'> => {
    if (!owner) return {};
    return owner.user_id ? { owner_user_id: owner.user_id } : { owner_email: owner.email ?? undefined };
  };

  const submitCreate = async () => {
    const body: CreateCodeBody = {
      kind: createKind,
      ...(code.trim() ? { code: code.trim().toUpperCase() } : {}),
      ...(createKind === 'promoter' ? ownerFields() : {}),
      ...(parsedRate !== null ? { payout_rate_cents: parsedRate } : {}),
      ...(campaignId ? { deal_campaign_id: campaignId } : {}),
      ...(ambassador.trim() ? { ambassador_ref: ambassador.trim() } : {}),
      status,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    };
    const saved = await create(body);
    if (saved) onSaved(saved, `Minted ${saved.code}.`);
  };

  const submitEdit = async () => {
    if (!existing) return;
    const body: UpdateCodeBody = {};

    if (!locked) {
      const originalOwnerId = existing.owner?.user_id ?? null;
      if (owner && !owner.user_id) {
        // Only an email was picked — the backend resolves it.
        body.owner_email = owner.email ?? undefined;
      } else if ((owner?.user_id ?? null) !== originalOwnerId) {
        body.owner_user_id = owner?.user_id ?? null;
      }

      if (parsedRate !== (existing.payout_rate_cents ?? null)) {
        body.payout_rate_cents = parsedRate;
      }

      const originalCampaign = existing.deal_campaign?.id ?? null;
      if ((campaignId || null) !== originalCampaign) {
        body.deal_campaign_id = campaignId || null;
      }
    }

    const originalAmbassador = existing.ambassador_ref ?? null;
    if ((ambassador.trim() || null) !== originalAmbassador) {
      body.ambassador_ref = ambassador.trim() || null;
    }
    if (status !== existing.status) body.status = status;

    if (Object.keys(body).length === 0) {
      setFormError('Nothing changed.');
      return;
    }
    if (reason.trim()) body.reason = reason.trim();

    const saved = await update(existing.code, body);
    if (saved) onSaved(saved, `Saved ${saved.code}.`);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = validate();
    setFormError(problem);
    if (problem) return;
    if (isEdit) await submitEdit();
    else await submitCreate();
  };

  const showOwner = effectiveKind === 'promoter' && !locked;
  // A campaign code credits nobody, so the rate is inert there — offered only
  // when one is already set, so an existing value stays visible and clearable.
  const showPayout =
    !locked && (effectiveKind === 'promoter' || existing?.payout_rate_cents != null);

  return (
    <form onSubmit={onSubmit} className="space-y-5 max-w-3xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            ← Cancel
          </button>
          <h3 className="text-lg font-semibold text-white">
            {isEdit ? (
              <span className="flex items-center gap-2">
                Edit <span className="font-mono">{existing!.code}</span> <CodeKindBadge kind={kind} />
              </span>
            ) : (
              'New referral code'
            )}
          </h3>
        </div>
      </div>

      {isPersonal && (
        <div className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-xs text-gray-400">
          This is a <b>personal</b> code minted by the app for one user. Only its status and ambassador
          tag can be edited here — owner, payout rate and deal are owned by the consumer.
        </div>
      )}

      {/* Kind */}
      {!isEdit && (
        <div>
          <FieldLabel required>Kind</FieldLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(['promoter', 'campaign'] as CreatableCodeKind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setCreateKind(k)}
                className={`text-left px-3.5 py-2.5 rounded-xl border transition-all ${
                  createKind === k
                    ? 'bg-violet-950/30 border-violet-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                }`}
              >
                <span className="text-sm font-medium">{k}</span>
                <span className="block text-xs text-gray-400 mt-1">{KIND_HELP[k]}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            Personal codes are not mintable here — the app creates one per user on demand.
          </p>
        </div>
      )}

      {/* Code */}
      {!isEdit && (
        <div>
          <FieldLabel>Vanity code</FieldLabel>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="SPRING-25 (leave blank to generate)"
            className={`${inputClass} font-mono`}
          />
          <p className="text-xs text-gray-500 mt-1">
            3–32 characters of A–Z, 0–9, '-' or '_'. Blank generates an 8-character code in the app's
            unambiguous alphabet.
          </p>
        </div>
      )}

      {/* Promoter binding */}
      {showOwner && (
        <UserPicker
          env={env}
          value={owner}
          onChange={setOwner}
          required
          label="Promoter (paid account)"
          directory={directory}
          helpText={
            promoterCodes.loading
              ? 'Loading known promoters…'
              : 'Search by name or email across existing promoters, or paste a user id / last 4 phone digits.'
          }
        />
      )}
      {effectiveKind === 'campaign' && !isEdit && (
        <div className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-xs text-gray-400">
          Campaign codes bind no account. Use the ambassador ref below to record who owns the campaign.
        </div>
      )}

      {/* Payout rate */}
      {showPayout && (
        <div>
          <FieldLabel>Payout rate per signup</FieldLabel>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
            <input
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
              className={`${inputClass} pl-7`}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Snapshotted onto each signup, so a change here applies to future signups only — it never
            moves what is already owed. Max {formatCents(PAYOUT_RATE_MAX_CENTS)}.
          </p>
          {effectiveKind === 'campaign' && (
            <p className="text-xs text-amber-400 mt-1">
              Campaign codes credit no account — this rate is recorded but pays nobody.
            </p>
          )}
        </div>
      )}

      {/* Deal attach */}
      {!locked && (
        <div>
          <FieldLabel>Attached deal</FieldLabel>
          <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className={selectClass}>
            <option value="">
              {isEdit ? 'No campaign — falls back to the default deal' : 'Default deal (fallback campaign)'}
            </option>
            {(campaigns.data?.campaigns ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.kind === 'personal_default' ? `${c.name} — fallback deal` : `${c.name} (${c.status})`}
              </option>
            ))}
          </select>
          {campaigns.error && <p className="text-xs text-red-400 mt-1">{campaigns.error}</p>}
          <p className="text-xs text-gray-500 mt-1">
            Deals are granted only from <b>active</b> campaigns inside their window.
          </p>
        </div>
      )}

      {/* Ambassador ref */}
      <div>
        <FieldLabel>Ambassador ref</FieldLabel>
        <input
          value={ambassador}
          onChange={(e) => setAmbassador(e.target.value)}
          placeholder="Promoter or agency tag"
          maxLength={AMBASSADOR_MAX}
          className={inputClass}
        />
      </div>

      {/* Status */}
      <div>
        <FieldLabel required>Status</FieldLabel>
        <select value={status} onChange={(e) => setStatus(e.target.value as CodeStatus)} className={selectClass}>
          <option value="active">active</option>
          <option value="disabled">disabled</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Disabling stops the code being redeemed; signups and payouts already recorded are unaffected.
        </p>
      </div>

      {/* Audit reason */}
      <div>
        <FieldLabel>Reason (audit note)</FieldLabel>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this code is being created / changed"
          className={inputClass}
        />
      </div>

      {formError && <ErrorBanner message={formError} />}
      {error && <ErrorBanner message={error} />}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40"
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Mint code'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium rounded-lg text-gray-400 hover:text-gray-200"
        >
          Cancel
        </button>
        {saving && <Spinner size="sm" />}
      </div>
    </form>
  );
}
